'use client';

import {
  AssessmentQuestion,
  AssessmentSection,
  AssessmentSubmission,
  SubmissionFile,
} from '@scaleup/models';
import { IconArrowLeft, IconArrowRight, IconCheck, IconLine } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { AssessmentQuestionInput } from './AssessmentQuestionInput';
import { Icon } from '@aws-amplify/ui-react';
import { Checkbox } from '@mantine/core';
import { RegistrationCheckUserRC } from '../../../../../apps/scaleup-platform-cdk/lib/api_gateway/routes/registration/user/index';

const maxPageQuestions = 3;

interface IProps {
  currentPage: number;
  submission: AssessmentSubmission | undefined;
  submissionLoading: boolean;
  section: AssessmentSection;
  changePage: (page: number) => void;
  sectionComplete: (complete: boolean) => void;
  setSubmission: (value: AssessmentSubmission) => void;
}

export const SectionQuestions = ({
  currentPage,
  submission,
  submissionLoading,
  section,
  changePage,
  sectionComplete,
  setSubmission,
}: IProps) => {
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
  const [subSection, setSubSection] = useState<string | undefined>('');
  const [lastPage, setLastPage] = useState(false);
  const [sectionCount, setSectionCount] = useState(0);
  const [questionNumbers, setQuestionNumbers] = useState<number[]>([]);
  const [dirty, setDirty] = useState(false);

  const onAnswerChange = (
    id: number,
    value: string,
    followup_activation?: string
  ) => {
    if (submission) {
      let temp: AssessmentSubmission = JSON.parse(JSON.stringify(submission));
      if (temp.answers[section.slug] && temp.answers[section.slug]?.[`${id}`]) {
        temp.answers[section.slug]![`${id}`]!.answer = value;
        temp.answers[section.slug]![`${id}`]!.answered = value.length > 0;
        setSubmission(temp);
        setDirty(true);
      }
    }
  };

  const getCurrentValue = (id: number): string => {
    const slug = section.slug;
    let value = '';
    if (submission) value = submission.answers[slug]?.[`${id}`]?.answer || '';
    return value;
  };

  const getCurrentFile = (id: number): SubmissionFile[] | undefined => {
    const slug = section.slug;
    if (submission) return submission.answers[slug]?.[`${id}`]?.files;
    return undefined;
  };

  const fileUpload = (id: number, file: File | null | undefined) => {
    if (submission) {
      let temp: AssessmentSubmission = JSON.parse(JSON.stringify(submission));
      if (temp.answers[section.slug] && temp.answers[section.slug]?.[`${id}`]) {
        if (temp.answers[section.slug]?.[`${id}`]?.files)
          temp.answers[section.slug]?.[`${id}`]?.files?.push({
            key: '',
            fileName: file?.name || '',
          });
        else
          temp.answers[section.slug]![`${id}`]!.files = [
            {
              key: '',
              fileName: file?.name || '',
            },
          ];
      }
      setSubmission(temp);
    }
  };

  const removeFile = (id: number, key: string) => {
    if (submission) {
      let temp: AssessmentSubmission = JSON.parse(JSON.stringify(submission));
      if (temp.answers[section.slug]?.[`${id}`]?.files) {
        let temp_files: SubmissionFile[] = [];
        temp.answers[section.slug]?.[`${id}`]?.files?.map(
          (file: SubmissionFile) => {
            if (file.fileName !== key) {
              temp_files.push(file);
            }
          }
        );
        temp.answers[section.slug]![`${id}`]!.files = temp_files;
      }

      setSubmission(temp);
    }
  };

  const checkSectionStatus = () => {
    if(dirty && submission?.answers[section.slug]){
      let completed = true;
      Object.keys(submission.answers[section.slug] || {}).forEach((key) => {
        completed = completed && (submission.answers[section.slug]?.[parseInt(key)]?.answered || false);
      });
      let temp: AssessmentSubmission = JSON.parse(JSON.stringify(submission));
      temp.sectionStatus[section.slug] = completed;
      setSubmission(temp);
      setDirty(false);
    }
  };

  useEffect(() => {
    let max_page = currentPage;
    let page = 0;
    let questions_list = section.questions;
    let index = 0;
    let questions: AssessmentQuestion[] = [];
    let indexes = [];
    let space_available = true;
    let sub;
    while (
      page < max_page &&
      index < questions_list.length &&
      space_available
    ) {
      sub = questions_list[index]?.['subSection'];
      questions = [];
      indexes = [];
      let counter = 0;
      while (counter < maxPageQuestions && index < questions_list.length) {
        let question = questions_list[index];
        if (question) {
          if (question['subSection'] != sub) {
            //space_available = false;
            break;
          }
          questions.push(question);
          index++;
          indexes.push(index);
          counter++;
        }
      }
      page++;
    }
    setSubSection(sub);
    setQuestions(questions);
    setQuestionNumbers(indexes);
    let totalPages = 0;
    for (let key in section['subSections']) {
      const subSectionCount = section['subSections']?.[key];
      if (subSectionCount !== undefined) {
        totalPages += Math.ceil(subSectionCount / maxPageQuestions);
      }
    }
    setLastPage(currentPage >= totalPages);
    setSectionCount(
      section.subSections[sub as keyof typeof section.subSections] || 0
    );
  }, [
    setSubSection,
    setQuestions,
    currentPage,
    section,
    setLastPage,
    setSectionCount,
    setQuestionNumbers,
  ]);

  return (
    <div className="">
      <div className="flex flex-col items-start gap-2.5 p-6 relative bg-white rounded-2xl overflow-hidden border border-solid border-color-styles-gray-200">
        <div className="flex flex-col items-start gap-6 relative self-stretch w-full flex-[0_0_auto]">
          <div className="flex flex-col items-start gap-6 relative self-stretch w-full flex-[0_0_auto]">
            <div className="flex items-start gap-6 relative self-stretch w-full flex-[0_0_auto]">
              <div className="relative flex-1 mt-[-1.00px] text-2xl font-bold text-[#1d2939]">
                {subSection}
              </div>
              <Badge
                className="badge-instance mr-3 mb-3"
                color="primary"
                icon={'false'}
                outline={false}
                size="lg"
                text={`${sectionCount} ${sectionCount == 1 ? 'Question' : 'Questions'}`}
                textClassName="design-component-instance-node"
              />
            </div>
            <div className="flex-col items-start overflow-auto w-full">
              <div className="items-center flex-[0_0_auto] flex relative w-full">
                <div className="flex items-start gap-2.5 px-0 py-4 relative flex-1 grow">
                  <div className="relative flex-1 mt-[-1.00px] text-xl font-bold text-[#1d2939]">
                    Question
                  </div>
                </div>
                <div className="items-start gap-2.5 pl-8 pr-0 py-0 flex-1 grow flex relative">
                  <div className="relative flex-1 mt-[-1.00px] text-xl text-center font-bold text-[#1d2939]">
                    Answer
                  </div>
                </div>
              </div>
              {questions.map((question, index) => {
                return (
                  <div key={question.id}>
                    <div className="flex items-center w-full gap-2 border-b-[1px] border-neutral-200">
                      <div className="py-4  w-1/2">
                        <p className="relative flex-1 mt-[-1.00px] font-text-md-regular font-[number:var(--text-md-regular-font-weight)] text-tokens-text-secondary text-[length:var(--text-md-regular-font-size)] tracking-[var(--text-md-regular-letter-spacing)] leading-[var(--text-md-regular-line-height)] [font-style:var(--text-md-regular-font-style)]">
                          {questionNumbers[index]}. {question.question}
                        </p>
                      </div>
                      <IconLine className="self-stretch w-px relative object-cover" />
                      <div className="flex items-center gap-2.5 pl-8 flex-1 self-stretch grow w-full mr-5">
                        <AssessmentQuestionInput
                          fileUpload={fileUpload}
                          getCurrentFile={getCurrentFile}
                          getCurrentValue={getCurrentValue}
                          onAnswerChange={onAnswerChange}
                          question={question}
                          removeFile={removeFile}
                          followup_activation={question.followUpActivation}
                          disabled={submission?.completed || false}
                        />
                        {question.followUpQuestions &&
                          getCurrentValue(question.id) ==
                            question.followUpActivation &&
                          question.followUpQuestions.map((followup, index) => (
                            <div className="mt-8" key={followup.id}>
                              <AssessmentQuestionInput
                                fileUpload={fileUpload}
                                getCurrentFile={getCurrentFile}
                                getCurrentValue={getCurrentValue}
                                onAnswerChange={onAnswerChange}
                                question={(() => {
                                  followup.sequence = parseFloat(
                                    (question.sequence || 1) + '.' + (index + 1)
                                  );
                                  return followup;
                                })()}
                                removeFile={removeFile}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-6 w-[100%] items-center justify-center">
            {currentPage > 1 && (
              <div className="col-start-1 col-span-1">
                <Button
                  variant="primary"
                  prefixIcon={<IconArrowLeft />}
                  text="Back"
                  onClick={() => changePage(currentPage - 1)}
                />
              </div>
            )}
            {!lastPage && (
              <div className="col-start-6 col-span-1">
                <Button
                  variant="primary"
                  suffixIcon={<IconArrowRight />}
                  text="Next"
                  onClick={() => changePage(currentPage + 1)}
                />
              </div>
            )}
            {lastPage && (
              <div className="col-start-6 col-span-1">
                <Button
                  variant="primary"
                  suffixIcon={<IconCheck />}
                  text="Finish"
                  onClick={() => {
                    sectionComplete(true);
                    checkSectionStatus();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/*
<div className="flex flex-col gap-8 animate-slideIn transition ease-in-out delay-150 opacity-0">
      <Card>
        <div className={`flex w-full items-center gap-3`}>
          <span className={'text-2xl font-bold'}>{subSection}</span>
          
        </div>
        <div className="services col-start-3 col-span-2">
          {questions.map((question) => {
            return (
              <div key={question.id}>
                <span>{question.question}</span>
              </div>
            );
          })}
        </div>
        {!lastPage && <Button
          variant="primary"
          suffixIcon="/icons/arrow_right.svg"
          text="Next"
          onClick={() => changePage(currentPage+1)}
        />}
        {lastPage && <Button
          variant="primary"
          suffixIcon="/icons/check.svg"
          text="Finish"
          onClick={() => sectionComplete(true)}
        />}
      </Card>
    </div>
*/
